import React, { useState } from 'react';
import './AdvancedSettingsSection.css';
import UniversalSectionContainer from './UniversalSectionContainer';
import ToggleSwitch from './ToggleSwitch';
import NumberInputSection from './NumberInputSection';
import SliderSection from './SliderSection';

function AdvancedSettingsSection({ 
    advancedConfig = [], 
    editData, 
    handleInputChange,
    handleSliderChange, // NEU: Für Slider-Events
    defaultExpanded = false
}) {

    // Gruppiere Settings nach Typ für intelligente Anordnung
    const groupedSettings = {
        dimensions: [],
        spacing: [],
        grid: [],
        video: [],
        symbols: [],
        behavior: [],
        display: []
    };

    // Automatische Kategorisierung basierend auf fieldName
    advancedConfig.forEach(setting => {
        const fieldName = setting.fieldName.toLowerCase();
        
        if (fieldName.includes('width') || fieldName.includes('height') || fieldName.includes('size')) {
            groupedSettings.dimensions.push(setting);
        } else if (fieldName.includes('margin') || fieldName.includes('padding') || fieldName.includes('spacing') || fieldName.includes('gap')) {
            groupedSettings.spacing.push(setting);
        } else if (fieldName.includes('perrow') || fieldName.includes('grid') || fieldName.includes('column')) {
            groupedSettings.grid.push(setting);
        } else if (fieldName.includes('video') || fieldName.includes('camera')) {
            groupedSettings.video.push(setting);
        } else if (fieldName.includes('symbol') || fieldName.includes('decorative')) {
            groupedSettings.symbols.push(setting);
        } else if (fieldName.includes('show') || fieldName.includes('enable') || fieldName.includes('invert') || fieldName.includes('auto')) {
            groupedSettings.behavior.push(setting);
        } else if (fieldName.includes('alignment') || fieldName.includes('align')) {
            groupedSettings.display.push(setting);
        } else {
            groupedSettings.display.push(setting);
        }
    });

    const renderSettingGroup = (title, settings, iconClass) => {
        if (settings.length === 0) return null;

        return (
            <div className="advanced-group">
                <div className="advanced-group-header">
                    <i className={`fa ${iconClass} advanced-group-icon`}></i>
                    <span className="advanced-group-title">{title}</span>
                </div>
                <div className="advanced-group-grid">
                    {settings.map((setting, index) => renderSetting(setting, index))}
                </div>
            </div>
        );
    };

    const renderSetting = (setting, index) => {
        const key = `${setting.fieldName}-${index}`;

        // Toggle Switches
        if (setting.type === 'toggle') {
            return (
                <div key={key} className="advanced-setting-item">
                    <div className="advanced-setting-header">
                        <label className="advanced-setting-label">{setting.label}</label>
                        {setting.icon && (
                            <img 
                                src={setting.icon} 
                                alt={setting.label}
                                className="advanced-setting-icon"
                                style={setting.iconStyle}
                            />
                        )}
                    </div>
                    <ToggleSwitch
                        fieldName={setting.fieldName}
                        checked={editData[setting.fieldName] ?? setting.defaultValue ?? false}
                        onChange={handleInputChange}
                        size="small"
                        color="var(--blue)"
                        showLabel={false}
                    />
                </div>
            );
        }

        // Custom Components (NEW)
        if (setting.type === 'custom' && setting.component) {
            const CustomComponent = setting.component;
            return (
                <div key={key} className="advanced-setting-item advanced-custom-item">
                    <CustomComponent
                        {...setting.componentProps}
                    />
                </div>
            );
        }

        // Select Dropdowns
        if (setting.type === 'select') {
            return (
                <div key={key} className="advanced-setting-item advanced-select-item">
                    <div className="advanced-setting-header">
                        <label className="advanced-setting-label">{setting.label}</label>
                        {setting.icon && (
                            <img 
                                src={setting.icon} 
                                alt={setting.label}
                                className="advanced-setting-icon"
                                style={setting.iconStyle}
                            />
                        )}
                    </div>
                    <select
                        name={setting.fieldName}
                        value={editData[setting.fieldName] ?? setting.defaultValue ?? ''}
                        onChange={handleInputChange}
                        className="advanced-select"
                    >
                        {setting.options?.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }

        // Slider Sections (mit Farben und Beschreibungen)
        if (setting.type === 'slider') {
            return (
                <div key={key} className="advanced-setting-item advanced-slider-item">
                    <SliderSection
                        label={setting.label}
                        fieldName={setting.fieldName}
                        value={editData[setting.fieldName] ?? setting.defaultValue ?? 0}
                        min={setting.min ?? 0}
                        max={setting.max ?? 100}
                        step={setting.step ?? 1}
                        unit={setting.unit ?? ''}
                        onChange={handleSliderChange || handleInputChange} // Slider braucht handleSliderChange
                        icon={setting.icon}
                        color={setting.color ?? '#8b5cf6'}
                        description={setting.description ?? ''}
                    />
                </div>
            );
        }

        // Number Inputs (einfache Inputs)
        return (
            <div key={key} className="advanced-setting-item">
                <div className="advanced-setting-header">
                    <label className="advanced-setting-label">{setting.label}</label>
                    {setting.icon && (
                        <img 
                            src={setting.icon} 
                            alt={setting.label}
                            className="advanced-setting-icon"
                            style={setting.iconStyle}
                        />
                    )}
                </div>
                <div className="advanced-number-input">
                    <input
                        type="number"
                        name={setting.fieldName}
                        value={editData[setting.fieldName] ?? setting.defaultValue ?? ''}
                        onChange={handleInputChange}
                        min={setting.min ?? 0}
                        max={setting.max ?? 1000}
                        step={setting.step ?? 1}
                        className="advanced-input"
                        placeholder={setting.defaultValue}
                    />
                    {setting.unit && (
                        <span className="advanced-unit">{setting.unit}</span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <UniversalSectionContainer 
            icon={<i className="fa fa-cogs"></i>}
            title="Advanced" 
            count={advancedConfig.length}
            gradient="default"
            defaultExpanded={defaultExpanded}
        >
            <div className="advanced-settings-grid">
                {renderSettingGroup('Dimensions', groupedSettings.dimensions, 'fa-expand-arrows-alt')}
                {renderSettingGroup('Spacing', groupedSettings.spacing, 'fa-arrows-alt-h')}
                {renderSettingGroup('Grid Layout', groupedSettings.grid, 'fa-th')}
                {renderSettingGroup('Video', groupedSettings.video, 'fa-video')}
                {renderSettingGroup('Symbols', groupedSettings.symbols, 'fa-star')}
                {renderSettingGroup('Behavior', groupedSettings.behavior, 'fa-cogs')}
                {renderSettingGroup('Display', groupedSettings.display, 'fa-eye')}
            </div>
        </UniversalSectionContainer>
    );
}

export default AdvancedSettingsSection;
